<template>
  <div class="carga-masiva flex-grow-1 p-4" style="background:#f8f9fa; overflow:auto">

    <!-- ── TOAST ── -->
    <transition name="fade-toast">
      <div
        v-if="toast.visible"
        class="pt-toast"
        :class="`pt-toast--${toast.variant}`"
      >
        <i
          class="fas mr-2"
          :class="{
            'fa-check-circle':         toast.variant === 'success',
            'fa-exclamation-circle':   toast.variant === 'danger',
            'fa-exclamation-triangle': toast.variant === 'warning',
          }"
        ></i>
        {{ toast.mensaje }}
      </div>
    </transition>

    <div class="row justify-content-center">
      <div class="col-lg-8">

        <!-- ── INSTRUCCIONES ── -->
        <div class="card mb-4">
  <div class="card-header d-flex align-items-center mb-2">
    <i class="fas fa-info-circle text-primary mr-2"></i>
    <strong>¿Cómo usar la carga masiva?</strong>
  </div>
  <div class="card-body">
    <ol class="mb-3">
      <li>Descarga la plantilla con el formato requerido.</li>
      <li>Llena/modifica los datos de productos y tiendas.</li>
      <li>Sube el archivo completado y espera la confirmación.</li>
    </ol>
    <div class="alert alert-info mb-3">
      <div class="d-flex align-items-center mb-2">
        <i class="fas fa-columns mr-2"></i>
        <strong>Las columnas  <code>id</code>, <code> producto_id</code> y <code>tienda_id</code> son obligatorias en el archivo</strong>
      </div>
      <ul class="mb-2 pl-4">
        <li>
          — El formato del <code>id</code> debe ser <code>producto_id-tienda_id</code>
          <span class="text-muted"> (ej: <code>A0001-02</code>)</span>
        </li>
      </ul>
      <small class="text-muted">
        <i class="fas fa-exclamation-circle mr-1"></i>
        Solo las celdas modificadas serán actualizadas.
      </small>
    </div>
    <button class="btn btn-outline-primary btn-sm" @click="descargarPlantilla">
      <i class="fas fa-download mr-1"></i> Descargar Excel desde metabase
    </button>
  </div>
</div>

        <!-- ── ESTADO: idle — selección de archivo ── -->
        <div class="card mb-4" v-if="estado === 'idle'">
          <div class="card-header">
            <strong>
              <i class="fas fa-file-excel text-success mr-2"></i>Subir archivo
            </strong>
          </div>
          <div class="card-body">

            <div
              class="pt-dropzone d-flex flex-column align-items-center justify-content-center p-5"
              :class="{
                'pt-dropzone--over':   dragging,
                'pt-dropzone--loaded': archivoSeleccionado
              }"
              @dragover.prevent="dragging = true"
              @dragleave="dragging = false"
              @drop.prevent="onDrop"
              @click="$refs.archivoInput.click()"
            >
              <input
                ref="archivoInput"
                type="file"
                accept=".xlsx,.xls"
                style="display:none"
                @change="onArchivoSeleccionado"
              />

              <template v-if="!archivoSeleccionado">
                <i class="fas fa-cloud-upload-alt fa-3x text-muted mb-3"></i>
                <p class="mb-1 font-weight-500">
                  Arrastra tu archivo aquí o haz clic para seleccionar
                </p>
                <small class="text-muted">Formatos aceptados: .xlsx, .xls</small>
              </template>

              <template v-else>
                <i class="fas fa-file-excel fa-3x text-success mb-2"></i>
                <p class="mb-1 font-weight-500">{{ archivoNombre }}</p>
                <small class="text-muted">
                  {{ (archivoSeleccionado.size / 1024).toFixed(1) }} KB
                </small>
                <button
                  class="btn btn-sm btn-outline-danger mt-2"
                  @click.stop="quitarArchivo"
                >
                  <i class="fas fa-times mr-1"></i> Quitar
                </button>
              </template>
            </div>

            <div class="mt-3 d-flex justify-content-end">
              <button
                class="btn btn-success"
                :disabled="!archivoSeleccionado"
                @click="subirArchivo"
              >
                <i class="fas fa-upload mr-1"></i> Subir y procesar
              </button>
            </div>

          </div>
        </div>

        <!-- ── ESTADO: subiendo ── -->
        <div class="card mb-4" v-else-if="estado === 'subiendo'">
          <div class="card-body text-center py-5">
            <div class="spinner-border text-success mb-3" role="status">
              <span class="sr-only">Subiendo...</span>
            </div>
            <p class="font-weight-bold mb-1">Subiendo archivo...</p>
            <small class="text-muted">Por favor espera.</small>
          </div>
        </div>

        <!-- ── ESTADO: procesando ── -->
        <div class="card mb-4" v-else-if="estado === 'procesando'">
          <div class="card-body py-4">

            <div class="d-flex align-items-center mb-3">
              <div class="spinner-border spinner-border-sm text-success mr-3" role="status">
                <span class="sr-only">Procesando...</span>
              </div>
              <span class="font-weight-bold">Procesando archivo en segundo plano...</span>
            </div>

            <div class="progress mb-4" style="height:8px">
              <div
                class="progress-bar progress-bar-striped progress-bar-animated bg-success"
                style="width:100%"
              ></div>
            </div>

            <div v-if="progreso" class="row text-center mb-3">
              <div class="col-4">
                <div class="pt-stat-card">
                  <div class="pt-stat-val">{{ progreso.total || '—' }}</div>
                  <div class="pt-stat-lbl">Total filas</div>
                </div>
              </div>
              <div class="col-4">
                <div class="pt-stat-card pt-stat-card--ok">
                  <div class="pt-stat-val text-success">{{ progreso.procesadas || '—' }}</div>
                  <div class="pt-stat-lbl">Procesadas</div>
                </div>
              </div>
              <div class="col-4">
                <div class="pt-stat-card pt-stat-card--err">
                  <div class="pt-stat-val text-danger">{{ progreso.fallidas || '—' }}</div>
                  <div class="pt-stat-lbl">Con error</div>
                </div>
              </div>
            </div>

            <div class="alert alert-info small mb-0">
              ¡Archivo en órbita! Estamos procesando los datos. Si tu lista es enorme, aprovecha para ir por un café. Te avisaremos al terminar.
             <i class="fas fa-coffee mr-1"></i>
            </div>

          </div>
        </div>

        <!-- ── ESTADO: completado ── -->
         <div v-else-if="estado === 'completado'" class="py-2">
          <div class="alert alert-success d-flex align-items-start mb-3">
            <i class="fas fa-check-circle fa-2x mr-3 mt-1"></i>
            <div>
              <strong>Carga completada.</strong>
            </div>
          </div>

          <!-- Resumen numérico -->
          <div class="row text-center mb-3" v-if="progreso">
            <div class="col-4">
              <div class="font-weight-bold text-dark h5 mb-0">{{ progreso.total_filas }}</div>
              <small class="text-muted">Total filas</small>
            </div>
            <div class="col-4">
              <div class="font-weight-bold text-success h5 mb-0">{{ progreso.filas_ok }}</div>
              <small class="text-muted">Actualizadas</small>
            </div>
            <div class="col-4">
              <div class="font-weight-bold text-danger h5 mb-0">{{ progreso.filas_con_error }}</div>
              <small class="text-muted">Con error</small>
            </div>
          </div>

          <!-- Detalle de errores -->
          <div
            v-if="progreso && progreso.errores_detalle && progreso.errores_detalle.length > 0"
            class="border rounded p-2"
            style="max-height: 200px; overflow-y: auto;"
          >
            <p class="font-weight-bold text-danger small mb-2">
              <i class="fas fa-exclamation-triangle mr-1"></i>
              Filas con error:
            </p>
            <ul class="list-unstyled mb-0">
              <li
                v-for="(err, idx) in progreso.errores_detalle"
                :key="idx"
                class="small text-danger border-bottom py-1"
              >
                <strong>Fila {{ err.fila }}</strong> | 
                <code>{{ err.id }}</code> → {{ err.error }}
              </li>
            </ul>
          </div>
          <div class="d-flex justify-content-end" style="gap:8px">
              <button class="btn btn-outline-success" @click="reiniciar">
                <i class="fas fa-redo mr-1"></i> Nueva carga
              </button>
            </div>
        </div>
        <!-- ── ESTADO: error ── -->
        <div class="card mb-4" v-else-if="estado === 'error'">
          <div class="card-body py-4">

            <div class="alert alert-danger d-flex align-items-start mb-4">
              <i class="fas fa-times-circle fa-2x mr-3 mt-1"></i>
              <div>
                <strong>Error al procesar el archivo.</strong>
                <p class="mb-0 small mt-1">{{ mensajeError }}</p>
              </div>
            </div>

            <div class="d-flex justify-content-end" style="gap:8px">
              <button class="btn btn-outline-danger" @click="reiniciar">
                <i class="fas fa-redo mr-1"></i> Reintentar
              </button>
            </div>

          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import DynamicService from '@/services/commons/DynamicService'
import Notifications from '@/notifications/notifications'

// Intervalo de polling en milisegundos
const POLLING_INTERVAL_MS = 3000

export default {
  name: 'CargaMasiva',

  data() {
    return {
      // idle | subiendo | procesando | completado | error
      estado: 'idle',

      archivoSeleccionado: null,
      archivoNombre: '',
      dragging: false,

      tareaId: null,
      progreso: null,
      mensajeError: '',

      _pollingInterval: null,

      toast: { visible: false, mensaje: '', variant: 'success', _timer: null },
    }
  },

  beforeDestroy() {
    // Limpieza al desmontar — la tarea Celery continúa en el backend
    this._detenerPolling()
  },

  methods: {

    // ── TOAST ─────────────────────────────────────────────────
    mostrarToast(mensaje, variant = 'success') {
      clearTimeout(this.toast._timer)
      this.toast.mensaje = mensaje
      this.toast.variant = variant
      this.toast.visible = true
      this.toast._timer  = setTimeout(() => { this.toast.visible = false }, 3500)
    },

    // ── ARCHIVO ───────────────────────────────────────────────
    onDrop(e) {
      this.dragging = false
      const file = e.dataTransfer.files[0]
      if (file) this.setArchivo(file)
    },

    onArchivoSeleccionado(e) {
      const file = e.target.files[0]
      if (file) this.setArchivo(file)
    },

    setArchivo(file) {
      const ext = file.name.split('.').pop().toLowerCase()
      if (!['xlsx', 'xls'].includes(ext)) {
        this.mostrarToast('Solo se aceptan archivos .xlsx o .xls', 'danger')
        return
      }
      this.archivoSeleccionado = file
      this.archivoNombre       = file.name
    },

    quitarArchivo() {
      this.archivoSeleccionado = null
      this.archivoNombre       = ''
      if (this.$refs.archivoInput) this.$refs.archivoInput.value = ''
    },

    // ── UPLOAD ────────────────────────────────────────────────
    async subirArchivo() {
      if (!this.archivoSeleccionado) return

      this.estado = 'subiendo'

      const formData = new FormData()
      formData.append('archivo', this.archivoSeleccionado)

      DynamicService.javascriptpostFormData('cat', 'carga-masiva', formData)
        .then(response => {
          this.tareaId = response.data.tarea_id
          this.estado  = 'procesando'
          this._iniciarPolling()
        })
        .catch(error => {
          const msg = error.response && error.response.data && error.response.data.detail || 'Error al subir el archivo.'
          this.estado       = 'error'
          this.mensajeError = msg
          Notifications.alert.error('Carga Masiva', msg)
        })
    },

    // ── POLLING ───────────────────────────────────────────────
    _iniciarPolling() {
      this._pollingInterval = setInterval(() => {
        this._consultarEstado()
      }, POLLING_INTERVAL_MS)
    },

    _detenerPolling() {
      if (this._pollingInterval) {
        clearInterval(this._pollingInterval)
        this._pollingInterval = null
      }
    },

    _consultarEstado() {
      if (!this.tareaId) return

      DynamicService.getAction('cat', 'carga-masiva', this.tareaId, 'estado')
        .then(response => {
          this.progreso = response.data

          if (response.data.estado === 'C') {
            this._detenerPolling()
            this.estado = 'completado'

            // Parseamos los errores_detalle 
            try {
              this.progreso = {
                ...response.data,
                errores_detalle: JSON.parse(response.data.errores_detalle || '[]')
              }
            } catch {
              this.progreso = { ...response.data, errores_detalle: [] }
            }

            this.$emit('carga-completada')

            // this.mostrarToast('Carga completada exitosamente.', 'success')
          } else if (response.data.estado === 'E') {
            this._detenerPolling()
            this.estado       = 'error'
            this.mensajeError = response.data.mensaje_error || 'Error desconocido.'
            Notifications.alert.error('Carga Masiva', this.mensajeError)
          }
        })
        .catch(() => {
          // Silencioso: si falla una consulta de polling no interrumpe el proceso.
          // Se reintentará en el siguiente tick.
        })
    },

    // ── REINICIAR ─────────────────────────────────────────────
    reiniciar() {
      this._detenerPolling()
      this.estado              = 'idle'
      this.archivoSeleccionado = null
      this.archivoNombre       = ''
      this.tareaId             = null
      this.progreso            = null
      this.mensajeError        = ''
      this.dragging            = false
      if (this.$refs.archivoInput) this.$refs.archivoInput.value = ''
    },

    // ── DESCARGA PLANTILLA ────────────────────────────────────
    descargarPlantilla() {
      const urls = {
        'Compras': 'http://192.168.250.10/question/1698-plantilla-actualizacion-productos-en-tienda?sku=&tienda=&categoria=&sub_categoria=',
        'Supply Chain': 'http://192.168.250.10/question/1698-plantilla-actualizacion-productos-en-tienda?sku=&tienda=&categoria=&sub_categoria=',
        'Admin': 'http://192.168.250.10/question/1698-plantilla-actualizacion-productos-en-tienda?sku=&tienda=&categoria=&sub_categoria=',
      };

      let grupos=[]
      try {
        grupos = JSON.parse(localStorage.getItem('grupos')) || [];
      } catch (e) {
        grupos = [];
      }

      const grupoConUrl = grupos.find((g) => urls[g.nombre]);
      const url = grupoConUrl ? urls[grupoConUrl.nombre] : null;
      
      if (!url) {
        Notifications.alert.error(
          'Descargar plantilla',
          'No se encontró una plantilla configurada para tu grupo.'
        );
        return;
      }

      window.open(url, '_blank');
    },
  },
}
</script>

<style scoped>
.font-weight-500 { font-weight: 500; }

/* ── Dropzone ── */
.pt-dropzone {
  border: 2px dashed #ced4da;
  border-radius: 8px;
  background: #f8f9fa;
  cursor: pointer;
  transition: border-color 0.2s, background 0.2s;
  min-height: 180px;
}
.pt-dropzone:hover,
.pt-dropzone--over   { border-color: #185FA5; background: #e8f1fb; }
.pt-dropzone--loaded { border-color: #28a745; background: #f0fff4; }

/* ── Stat cards ── */
.pt-stat-card {
  padding: 12px;
  border-radius: 6px;
  background: #f8f9fa;
  border: 1px solid #dee2e6;
}
.pt-stat-card--ok  { border-color: #c3e6cb; background: #f0fff4; }
.pt-stat-card--err { border-color: #f5c6cb; background: #fff5f5; }
.pt-stat-val { font-size: 26px; font-weight: 600; }
.pt-stat-lbl { font-size: 11px; color: #6c757d; margin-top: 2px; }

/* ── Toast ── */
.pt-toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  z-index: 9999;
  padding: 10px 18px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #fff;
  box-shadow: 0 4px 12px rgba(0,0,0,0.15);
}
.pt-toast--success { background: #28a745; }
.pt-toast--danger  { background: #dc3545; }
.pt-toast--warning { background: #e6a817; }

.fade-toast-enter-active, .fade-toast-leave-active { transition: opacity 0.3s, transform 0.3s; }
.fade-toast-enter, .fade-toast-leave-to { opacity: 0; transform: translateY(10px); }

/* Spinner */
.spinner-border { width: 2.5rem; height: 2.5rem; }
.spinner-border-sm { width: 1rem; height: 1rem; border-width: 0.15em; }
</style>